import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { ImageWithPlaceholder } from '../ImageWithPlaceholder/ImageWithPlaceholder';
import { StarRating } from '../StarRating/StarRating';
import { getImageUrl } from '../../lib/supabase';

/**
 * Yelp-style review card — user avatar (initials), star rating,
 * date line, review text, and product thumbnail.
 */
export function TestimonialCard({ testimonial, index = 0 }) {
  const productImageUrl = testimonial.products?.image_path
    ? getImageUrl(testimonial.products.image_path)
    : null;

  // Generate initials for avatar
  const initials = testimonial.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-brand-white rounded-lg border border-ui-border p-4 sm:p-5 hover:shadow-sm transition-shadow"
    >
      {/* Top row — avatar + name + rating */}
      <div className="flex items-start gap-3">
        {/* Avatar circle with initials */}
        <div className="w-10 h-10 rounded-full bg-brand-red flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-brand-white">{initials}</span>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-brand-dark">{testimonial.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <StarRating rating={testimonial.rating || 5} size={14} />
            <span className="text-xs text-brand-gray">
              {new Date(testimonial.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Review text */}
      <p className="mt-3 text-sm text-brand-dark leading-relaxed">
        {testimonial.text}
      </p>

      {/* Product reference + thumbnail — Yelp-style "reviewed" link */}
      {testimonial.products?.title && (
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-ui-border">
          {productImageUrl && (
            <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 border border-ui-border">
              <ImageWithPlaceholder
                src={productImageUrl}
                alt={testimonial.products.title}
                aspectRatio="aspect-square"
                className="w-full h-full"
              />
            </div>
          )}
          <span className="text-xs text-brand-gray">
            Reviewed <span className="font-semibold text-brand-dark">{testimonial.products.title}</span>
          </span>
        </div>
      )}
    </motion.div>
  );
}
